import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Package, Loader2, RefreshCw, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { STATUS_STYLES, gbp } from "../../lib/constants";

export default function Orders() {
  const navigate = useNavigate();
  const { addItems } = useCart();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/customer/orders")
      .then((r) => setOrders(r.data))
      .catch((err) => {
        if (err.response?.status !== 401) toast.error("Couldn't load your orders. Please try again.");
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const fmt = (d) =>
    new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/London" });

  const reorder = (o) => {
    addItems(
      o.items.map((it) => ({
        product_id: it.product_id,
        product_name: it.product_name,
        price: it.price,
        unit: it.unit,
        quantity: it.quantity,
      }))
    );
    toast.success("Items added to your basket");
    navigate("/cart");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 pt-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-bold text-[#0A1F13] tracking-tight">My Orders</h1>
        <button data-testid="refresh-orders" onClick={load} className="p-2 rounded-full hover:bg-[#E8F5E9] text-[#1A4331] transition-colors">
          <RefreshCw size={18} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#228B22]" size={32} /></div>
      ) : orders.length === 0 ? (
        <div data-testid="empty-orders" className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-[#E8F5E9] flex items-center justify-center mb-5">
            <Package size={34} className="text-[#228B22]" />
          </div>
          <h2 className="font-display text-2xl font-bold text-[#0A1F13]">No orders yet</h2>
          <p className="text-gray-500 mt-2">Your placed orders will show up here.</p>
          <button data-testid="start-shopping-button" onClick={() => navigate("/home")} className="mt-6 px-6 h-12 rounded-full bg-[#228B22] hover:bg-[#1A4331] text-white font-semibold transition-colors duration-200">Start shopping</button>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => {
            const isOpen = expanded === o.id;
            return (
              <div key={o.id} data-testid={`order-${o.id}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button
                  data-testid={`order-toggle-${o.id}`}
                  onClick={() => setExpanded(isOpen ? null : o.id)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-gray-400">#{o.id.slice(-6).toUpperCase()}</span>
                      <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${STATUS_STYLES[o.status] || ""}`}>{o.status}</span>
                    </div>
                    <p className="text-sm text-gray-500">{fmt(o.created_at)}</p>
                    <p className="text-sm text-gray-400 mt-0.5">{o.items.length} item{o.items.length > 1 ? "s" : ""}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-[#0A1F13]">{gbp(o.total_amount)}</span>
                    <ChevronDown size={18} className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                  </div>
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 border-t border-gray-100 pt-4">
                    <div className="space-y-2">
                      {o.items.map((it, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-600">{it.product_name} × {it.quantity}</span>
                          <span className="font-medium text-[#0A1F13]">{gbp(it.subtotal)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-sm">
                      <div className="flex justify-between text-gray-500"><span>Subtotal</span><span>{gbp(o.subtotal)}</span></div>
                      <div className="flex justify-between text-gray-500"><span>Fixed Charge</span><span>{gbp(o.fixed_charge)}</span></div>
                      <div className="flex justify-between text-gray-500"><span>Delivery ({o.delivery_zone})</span><span>{gbp(o.outside_dorchester_charge)}</span></div>
                      <div className="flex justify-between font-bold text-[#0A1F13] pt-1"><span>Total</span><span>{gbp(o.total_amount)}</span></div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-100 text-sm text-gray-500">
                      <p>{o.delivery_name} · {o.delivery_phone}</p>
                      <p>{o.delivery_address}, {o.delivery_postcode}</p>
                    </div>
                    <button
                      data-testid={`reorder-${o.id}`}
                      onClick={() => reorder(o)}
                      className="mt-4 w-full h-11 rounded-full border border-[#228B22] text-[#228B22] font-semibold flex items-center justify-center gap-2 hover:bg-[#E8F5E9] transition-colors duration-200"
                    >
                      <RotateCcw size={17} /> Order Again
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="h-8" />
    </div>
  );
}
